import React from 'react'
import config from 'config'
import { FormattedMessage } from 'translations';
import TranslatedField from 'app/components/common/TranslatedField';
import SwapIcon from 'mz-icons/swap'
import { Row, Cell } from 'mz-ui-kit/core/Row';
import PlaceField from 'mz-ui-kit/form/PlaceField';
import DateField from 'mz-ui-kit/form/DateField';
import PassengersNumberField from 'mz-ui-kit/form/PassengersNumberField';
import TimeField from 'mz-ui-kit/form/TimeField';
import RideDurationField from 'mz-ui-kit/form/RideDurationField';
import RoundtripSwitchField from '../RoundtripSwitchField'
import SearchButton from '../SearchButton';
import { SwapLocations, Container } from './elements';


const messages = {
  fromLabel: { id: 'SEARCH_FORM.FROM' },
  toLabel: { id: 'SEARCH_FORM.TO' },
  addressPlaceholder: { id: config.ENABLE_TRAIN_WORDING
    ? 'SEARCH_FORM.ADDRESS_PLACEHOLDER_TRAIN'
    : 'SEARCH_FORM.ADDRESS_PLACEHOLDER'
  },
  passengersLabel: { id: 'SEARCH_FORM.NUMBER_PASSENGERS' },
  departureLabel: { id: 'SEARCH_FORM.PICKUP_DATE' },
  departureTime: { id: 'SEARCH_FORM.PICKUP_TIME' },
  returnLabel: { id: 'SEARCH_FORM.RETURN_PICKUP_DATE' },
  returnTime: { id: 'SEARCH_FORM.RETURN_PICKUP_TIME' },
  roundtripLabel: { id: 'SEARCH_FORM.ROUND_TRIP' },
  rideDuration: { id: 'SEARCH_FORM.RIDE_DURATION' }
};

const DEFAULT_LOCATION_BIAS = config.PLACE_AUTOCOMPLETE_PARAMS
  .reduce((acc, p) => ({ ...acc, [p[0]]: p[1] }), {})

const ResponsiveSearchForm = ({
  fieldAppearance,
  searching,
  withSubmitButton,
  onSwapLocations,
  anyTouched,
  isHourlyRideSearch,
  isRoundTrip,
  endAddress,
  startAddress,
  pickupDatetime,
  placeParams,
}) => (
  <Container>
    <Row wrap="inf" innerBorder="gray">
      <Row wrap="xs" innerBorder="gray">
        <Cell>
          <TranslatedField
            component={PlaceField}
            name="start_address"
            appearance={fieldAppearance}
            disableFloatingLabel
            disabled={searching}
            hideErrors
            selectable={false}
            label={<FormattedMessage {...messages.fromLabel} />}
            placeholder={<FormattedMessage {...messages.addressPlaceholder} />}
            bias={{ ...endAddress, ...DEFAULT_LOCATION_BIAS }}
            otherParams={placeParams}
          />
          {!isHourlyRideSearch && (
            <SwapLocations $disabled={searching} onClick={onSwapLocations}>
              <SwapIcon />
            </SwapLocations>
          )}
        </Cell>
        {!isHourlyRideSearch && (
          <TranslatedField
            component={PlaceField}
            name="end_address"
            appearance={fieldAppearance}
            disableFloatingLabel
            disabled={searching}
            hideErrors
            selectable={false}
            label={<FormattedMessage {...messages.toLabel} />}
            placeholder={<FormattedMessage {...messages.addressPlaceholder} />}
            bias={{ ...startAddress, ...DEFAULT_LOCATION_BIAS }}
            otherParams={{
              ...placeParams,
              strictbounds: true,
              lat: startAddress?.lat,
              lng: startAddress?.lng,
            }}
          />
        )}
        {isHourlyRideSearch && (
          <TranslatedField
            component={RideDurationField}
            name="hourly_booking_duration"
            appearance={fieldAppearance}
            label={<FormattedMessage {...messages.rideDuration} />}
            disabled={searching}
            selectable={false}
          />
        )}
      </Row>

      <Row cols={20} wrap={isRoundTrip ? 'sm' : 'xs'} innerBorder="gray">
        <Cell col={isRoundTrip ? 14 : 10}>
          <Row wrap="xs" innerBorder="gray">
            <Row horizontalGutter={false}>
              <TranslatedField
                component={DateField}
                name="pickup_datetime"
                appearance={fieldAppearance}
                disableFloatingLabel
                disabled={searching}
                hideErrors
                selectable={false}
                cleanable={false}
                label={<FormattedMessage {...messages.departureLabel} />}
                placeholder={<FormattedMessage {...messages.departureLabel} />}
                lightboxSubtitle={<span />}
                id="pickup_date"
              />
              <TranslatedField
                component={TimeField}
                name="pickup_datetime"
                appearance={fieldAppearance}
                disableFloatingLabel
                disabled={searching}
                hideErrors
                selectable={false}
                cleanable={false}
                label={<FormattedMessage {...messages.departureTime} />}
                placeholder={<FormattedMessage {...messages.departureTime} />}
                lightboxSubtitle={<span />}
              />
            </Row>
            {isRoundTrip && !isHourlyRideSearch && (
              <Row horizontalGutter={false}>
                <TranslatedField
                  component={DateField}
                  name="return_pickup_datetime"
                  appearance={fieldAppearance}
                  disableFloatingLabel
                  disabled={searching}
                  hideErrors
                  selectable={false}
                  cleanable={false}
                  minDate={pickupDatetime}
                  label={<FormattedMessage {...messages.returnLabel} />}
                  placeholder={<FormattedMessage {...messages.returnLabel} />}
                  lightboxSubtitle={<span />}
                />
                <TranslatedField
                  component={TimeField}
                  name="return_pickup_datetime"
                  appearance={fieldAppearance}
                  disableFloatingLabel
                  disabled={searching}
                  hideErrors
                  selectable={false}
                  cleanable={false}
                  label={<FormattedMessage {...messages.returnTime} />}
                  placeholder={<FormattedMessage {...messages.returnTime} />}
                  lightboxSubtitle={<span />}
                />
              </Row>
            )}
          </Row>
        </Cell>

        <Cell col={isRoundTrip ? 6 : 10}>
          <Row cols={20} horizontalGutter={false}>
            <Cell col={10}>
              <TranslatedField
                component={PassengersNumberField}
                name="num_passengers"
                appearance={fieldAppearance}
                disableFloatingLabel
                disabled={searching}
                hideErrors
                label={<FormattedMessage {...messages.passengersLabel} />}
              />
            </Cell>
            <Cell col={6}>
              {!isHourlyRideSearch && (
                <TranslatedField
                  appearance={fieldAppearance}
                  component={RoundtripSwitchField}
                  name="mode"
                  label={<FormattedMessage {...messages.roundtripLabel} />}
                  activeValue="round_trip"
                  inactiveValue="one_way"
                  disabled={searching}
                />
              )}
            </Cell>
            {withSubmitButton && (
              <Cell col={4} valign="center">
                <SearchButton
                  disabled={!anyTouched || searching}
                />
              </Cell>
            )}
          </Row>
        </Cell>
      </Row>
    </Row>
  </Container>
)

export default ResponsiveSearchForm
